import { useState } from "react";

import { BadgeCheck, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSoftwareInstanceMutations } from "@/features/servers/hooks/useCenterInfra";
import type {
  IHost,
  ISoftwareInstance,
} from "@/features/servers/schemas/servers";
import {
  buildHostVerificationPayload,
  getVerifierLabel,
} from "@/features/servers/utils/host-verification";
import { isVersionOutdated } from "@/features/servers/utils/semver";
import { useCurrentUser } from "@/features/users/hooks/useCurrentUser";
import { cn } from "@/shared/lib/utils";

interface FichaSoftwareCardProps {
  institutionUid: string;
  instance: ISoftwareInstance;
  typeLabel: string;
  host?: IHost;
  targetVersion?: string;
}

/**
 * Tarjeta de software en ficha de consulta con verificación rápida.
 */
export function FichaSoftwareCard({
  institutionUid,
  instance,
  typeLabel,
  host,
  targetVersion,
}: FichaSoftwareCardProps) {
  const { data: currentUser } = useCurrentUser();
  const { update } = useSoftwareInstanceMutations(institutionUid);
  const [verifying, setVerifying] = useState(false);

  const outdated =
    !!instance.version &&
    !!targetVersion &&
    isVersionOutdated(instance.version, targetVersion);

  const accent = !instance.verifiedAt
    ? "border-l-amber-500"
    : outdated
      ? "border-l-destructive"
      : instance.active
        ? "border-l-emerald-500"
        : "border-l-border";

  const handleVerify = async () => {
    const by = getVerifierLabel(currentUser);
    if (!by) {
      toast.error("No se pudo identificar al verificador");
      return;
    }
    setVerifying(true);
    try {
      await update.mutateAsync({
        instanceId: instance.id,
        data: buildHostVerificationPayload(by),
      });
      toast.success(`"${typeLabel}" verificado`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al verificar software"
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <article
      className={cn(
        "rounded-lg border border-l-4 bg-card p-3 space-y-2.5 transition-colors hover:bg-muted/20",
        accent
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Package className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <h3 className="font-medium text-sm truncate">{typeLabel}</h3>
          </div>
          <div className="flex flex-wrap gap-1">
            <Badge variant={instance.active ? "default" : "secondary"}>
              {instance.active ? "Activo" : "Inactivo"}
            </Badge>
            {instance.version && (
              <Badge variant="outline">v{instance.version}</Badge>
            )}
            {targetVersion && (
              <Badge variant={outdated ? "destructive" : "secondary"}>
                Target {targetVersion}
                {outdated ? " · desactualizado" : ""}
              </Badge>
            )}
            {host && <Badge variant="outline">Host: {host.name}</Badge>}
          </div>
        </div>
        <Button
          size="sm"
          variant={instance.verifiedAt ? "outline" : "default"}
          className="shrink-0 cursor-pointer print:hidden h-8"
          onClick={() => void handleVerify()}
          disabled={verifying || update.isPending}
          aria-label={
            instance.verifiedAt
              ? `Re-verificar ${typeLabel}`
              : `Verificar ${typeLabel}`
          }
        >
          {verifying ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
          ) : (
            <BadgeCheck className="h-3.5 w-3.5 mr-1" aria-hidden />
          )}
          {instance.verifiedAt ? "Re-verificar" : "Verificar"}
        </Button>
      </div>

      <dl className="grid gap-x-3 gap-y-1.5 text-xs sm:grid-cols-2">
        {instance.mail && (
          <div>
            <dt className="text-muted-foreground">Mail</dt>
            <dd className="truncate">{instance.mail}</dd>
          </div>
        )}
        {instance.url && (
          <div>
            <dt className="text-muted-foreground">URL</dt>
            <dd className="truncate font-mono">{instance.url}</dd>
          </div>
        )}
        {instance.lastUpdatedAt && (
          <div>
            <dt className="text-muted-foreground">Último update</dt>
            <dd>{instance.lastUpdatedAt}</dd>
          </div>
        )}
        {instance.password && (
          <div>
            <dt className="text-muted-foreground">Clave</dt>
            <dd className="font-mono">{instance.password}</dd>
          </div>
        )}
      </dl>

      {instance.verifiedAt ? (
        <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
          Verificado: {instance.verifiedAt}
          {instance.verifiedBy ? ` · ${instance.verifiedBy}` : ""}
        </p>
      ) : (
        <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
          Sin verificar
        </p>
      )}

      {instance.dicomEndpoints.length > 0 && (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
            Endpoints DICOM
          </p>
          <ul className="space-y-0.5">
            {instance.dicomEndpoints.map((ep, i) => (
              <li key={i} className="text-xs font-mono">
                {ep.label ? `${ep.label}: ` : ""}
                {ep.aetitle}:{ep.port}
                {ep.ip ? `@${ep.ip}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {instance.customFields.map((f, i) => (
        <p key={i} className="text-xs">
          <span className="font-medium">{f.key}:</span> {f.value}
        </p>
      ))}
    </article>
  );
}
